// Lightweight MetaMask wallet helpers. UI mock-friendly: works against window.ethereum
// when MetaMask is installed, and surfaces clear errors when not.

export const hasMetaMask = () => {
  try {
    return typeof window !== "undefined"
      && !!window.ethereum
      && !!window.ethereum.isMetaMask;
  } catch (_) {
    // Some sandboxed iframes throw on property access — treat as "absent".
    return false;
  }
};

export async function connectMetaMask() {
  if (!hasMetaMask()) {
    // User-spec copy — "Wallet extension not detected…"
    throw new Error(
      "Wallet extension not detected. Install MetaMask or open this page in a wallet browser.",
    );
  }
  const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
  const chainIdHex = await window.ethereum.request({ method: "eth_chainId" });
  return {
    address: accounts?.[0],
    chainId: parseInt(chainIdHex, 16),
  };
}

export async function getCurrentChainId() {
  if (!hasMetaMask()) return null;
  const hex = await window.ethereum.request({ method: "eth_chainId" });
  return parseInt(hex, 16);
}

/**
 * Switch (or add+switch) the connected wallet to a given EVM chain.
 * Read-only intent: this only changes the user's active network in MetaMask.
 * No transactions, no approvals, no signatures.
 */
export async function switchChain({ chainId, chainIdHex, chainName, rpcUrl, blockExplorerUrl, nativeCurrency }) {
  if (!hasMetaMask()) throw new Error("MetaMask not detected");
  const targetHex = chainIdHex || `0x${Number(chainId).toString(16)}`;
  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: targetHex }],
    });
  } catch (err) {
    // 4902 = chain not added in MetaMask. Add it then retry.
    if (err?.code === 4902 || /Unrecognized chain ID/i.test(err?.message || "")) {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: targetHex,
          chainName: chainName || "BNB Smart Chain",
          rpcUrls: [rpcUrl || "https://bsc-dataseed.binance.org"],
          blockExplorerUrls: [blockExplorerUrl || "https://bscscan.com"],
          nativeCurrency: nativeCurrency || { name: "BNB", symbol: "BNB", decimals: 18 },
        }],
      });
    } else {
      throw err;
    }
  }
}

export function shortAddress(addr) {
  if (!addr) return "";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

export function onAccountsChanged(cb) {
  // Defensive: only register if the provider implements an EIP-1193
  // event emitter. Some non-MetaMask wallets falsely set
  // `isMetaMask: true` but don't expose `.on()` — registering would
  // crash the whole app.
  if (!hasMetaMask()) return () => {};
  const eth = (typeof window !== "undefined") ? window.ethereum : null;
  if (!eth || typeof eth.on !== "function") return () => {};
  const handler = (accounts) => cb(accounts?.[0] ?? null);
  try {
    eth.on("accountsChanged", handler);
  } catch (_) {
    return () => {};
  }
  return () => {
    try { eth.removeListener?.("accountsChanged", handler); } catch (_) {}
  };
}

export function onChainChanged(cb) {
  if (!hasMetaMask()) return () => {};
  const eth = (typeof window !== "undefined") ? window.ethereum : null;
  if (!eth || typeof eth.on !== "function") return () => {};
  const handler = (hex) => cb(parseInt(hex, 16));
  try {
    eth.on("chainChanged", handler);
  } catch (_) {
    return () => {};
  }
  return () => {
    try { eth.removeListener?.("chainChanged", handler); } catch (_) {}
  };
}
